/**
 * Voice AI — IVR Service
 * Owns all IVR CRUD API calls. Components call this service
 * instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class IvrService {

  /* ─── Menus CRUD ─── */

  async listMenus(opts = {}, { signal } = {}) {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.status) params.set('status', opts.status);
    const fetchOpts = {};
    if (signal) fetchOpts.signal = signal;
    const res = await authService.apiFetch(`${API_BASE}/ivr-menus.php?${params}`, fetchOpts);
    return res.json();
  }

  async getMenu(id) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-menus.php?id=${id}`);
    return res.json();
  }

  async createMenu(data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-menus.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async updateMenu(id, data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-menus.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async deleteMenu(id) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-menus.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  /* ─── Options CRUD ─── */

  async listOptions(menuId) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-options.php?menu_id=${menuId}`);
    return res.json();
  }

  async createOption(data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-options.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async updateOption(id, data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-options.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async deleteOption(id) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-options.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  async reorderOptions(menuId, order) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-options-reorder.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ menu_id: menuId, order }),
    });
    return res.json();
  }

  /* ─── Extensions CRUD ─── */

  async listExtensions() {
    const res = await authService.apiFetch(`${API_BASE}/ivr-extensions.php`);
    return res.json();
  }

  async createExtension(data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-extensions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async updateExtension(id, data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-extensions.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async deleteExtension(id) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-extensions.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  /* ─── Schedules CRUD ─── */

  async listSchedules(menuId) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-schedules.php?menu_id=${menuId}`);
    return res.json();
  }

  async createSchedule(data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-schedules.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async updateSchedule(id, data) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-schedules.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  async deleteSchedule(id) {
    const res = await authService.apiFetch(`${API_BASE}/ivr-schedules.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  /* ─── Transform Helpers ─── */

  transformMenu(m) {
    return {
      id: m.id,
      name: m.name,
      description: m.description || '',
      status: m.status || 'draft',
      greetingType: m.greeting_type || 'tts',
      greetingTts: m.greeting_tts || '',
      greetingAudioId: m.greeting_audio_id || null,
      greetingEnabled: m.greeting_enabled ?? 1,
      promptType: m.prompt_type || 'tts',
      promptTts: m.prompt_tts || '',
      promptAudioId: m.prompt_audio_id || null,
      promptEnabled: m.prompt_enabled ?? 1,
      invalidInputTts: m.invalid_input_tts || 'Sorry, that is not a valid option.',
      timeoutSeconds: m.timeout_seconds || 5,
      maxRetries: m.max_retries || 3,
      timeoutAction: m.timeout_action || 'repeat',
      timeoutAgentId: m.timeout_agent_id || null,
      timeoutTransferNumber: m.timeout_transfer_number || '',
      voiceName: m.voice_name || 'Polly.Joanna',
      voiceLanguage: m.voice_language || 'en-US',
      holdMusicAudioId: m.hold_music_audio_id || null,
      holdMusicType: m.hold_music_type || 'audio',
      holdMusicTts: m.hold_music_tts || '',
      holdMusicEnabled: m.hold_music_enabled ?? 1,
      enableExtensionDialing: m.enable_extension_dialing ?? 1,
      optionCount: m.option_count || 0,
      phoneCount: m.phone_count || 0,
      scheduleCount: m.schedule_count || 0,
      options: m.options || [],
      schedules: m.schedules || [],
      phoneNumbers: m.phone_numbers || [],
      createdAt: m.created_at,
      updatedAt: m.updated_at,
    };
  }

  toSnakeCase(menu) {
    return {
      name: menu.name,
      description: menu.description,
      status: menu.status,
      greeting_type: menu.greetingType,
      greeting_tts: menu.greetingTts,
      greeting_audio_id: menu.greetingAudioId,
      greeting_enabled: menu.greetingEnabled,
      prompt_type: menu.promptType,
      prompt_tts: menu.promptTts,
      prompt_audio_id: menu.promptAudioId,
      prompt_enabled: menu.promptEnabled,
      invalid_input_tts: menu.invalidInputTts,
      timeout_seconds: menu.timeoutSeconds,
      max_retries: menu.maxRetries,
      timeout_action: menu.timeoutAction,
      timeout_agent_id: menu.timeoutAgentId,
      timeout_transfer_number: menu.timeoutTransferNumber,
      voice_name: menu.voiceName,
      voice_language: menu.voiceLanguage,
      hold_music_audio_id: menu.holdMusicAudioId,
      hold_music_type: menu.holdMusicType,
      hold_music_tts: menu.holdMusicTts,
      hold_music_enabled: menu.holdMusicEnabled,
      enable_extension_dialing: menu.enableExtensionDialing,
    };
  }

  relativeTime(dateStr) {
    if (!dateStr) return 'Never';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return `${Math.floor(diffDay / 30)}mo ago`;
  }
}

export const ivrService = new IvrService();
